import React, { useState, useEffect, useCallback, useRef } from 'react';
import { SearchInput } from '../components/SearchInput';
import { SearchFiltersPanel } from '../components/SearchFilters';
import { SearchResultItem } from '../components/SearchResultItem';
import { DocumentPreviewModal } from '../components/DocumentPreviewModal';
import { searchService } from '../services/searchService';
import api from '../services/api';
import type { SearchParams, SearchResponse, SearchFilters as SearchFiltersType } from '../types/search';
import { ChevronLeft, ChevronRight, SortAsc, SortDesc, Grid, List, Search as SearchIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { documentService } from '../services/documentService';

// Note: Multiple facet loads are now prevented by debouncing in searchService

const Search: React.FC = () => {
  // Search state
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResponse | null>(null);
  const [filteredDocuments, setFilteredDocuments] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchParams, setSearchParams] = useState<SearchParams>({
    query: '',
    page: 1,
    per_page: 20,
    filters: {},
    sort: 'score',
    order: 'desc'
  });

  // UI state
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [sortOpen, setSortOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<any>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  // Favorites state (in a real app, this would come from backend)
  const [favorites, setFavorites] = useState<Set<number>>(new Set());

  // Ref to track if facets have been loaded to prevent infinite loops
  const facetsLoadedRef = useRef(false);

  // Component lifecycle logging
  useEffect(() => {
    console.log('Search component mounted');
    const loadFavorites = async () => {
      try {
        const userFavorites = await documentService.getFavorites();
        // The backend returns an array of objects: [{ document: { id: 1, ... } }, ...]
        // We need to extract just the IDs for our Set
        const favoriteIds = new Set(userFavorites.map((fav: any) => fav.document.id));
        setFavorites(favoriteIds);
      } catch (error) {
        console.error('Failed to load favorites', error);
      }
    };

    loadFavorites();

    return () => {
      console.log('Search component unmounted');
    };
  }, []);

  // Global flag to prevent multiple facet loads across component instances
  // This is needed because React StrictMode causes double-rendering in development
  const [facetsLoading, setFacetsLoading] = useState(false);

  // Search function - memoized to prevent infinite loops
  const performSearch = useCallback(async (params: SearchParams) => {
    if (!params.query.trim()) return;

    setIsSearching(true);
    try {
      const results = await searchService.search(params);
      setSearchResults(results);
      setSearchParams(params);
      setFilteredDocuments(results.documents);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Search failed');
      console.error('Search error:', error);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Handle search submission
  const handleSearch = useCallback(() => {
    const newParams = { ...searchParams, query, page: 1 };
    performSearch(newParams);
  }, [searchParams, query, performSearch]);

  // Handle filter changes
  const handleFiltersChange = useCallback((filters: SearchFiltersType) => {
    console.log('Filters changed:', filters);

    // 1. Update params just to keep UI in sync (optional)
    setSearchParams(prev => ({ ...prev, filters }));

    if (!searchResults) return;

    // 2. Filter the Master List locally
    const filtered = searchResults.documents.filter(doc => {
      // Check Document Type
      if (filters.document_type && doc.document_type !== filters.document_type) return false;

      // Check Publisher
      if (filters.publisher && doc.publisher !== filters.publisher) return false;

      // Check Court
      if (filters.court && doc.court !== filters.court) return false;

      // ... add other checks ...

      // Check File Type
      if (filters.file_type && doc.file_type !== filters.file_type) return false;

      // Check Date From
      if (filters.date_from && new Date(doc.created_at) <= new Date(filters.date_from)) return false;

      // Check Date To
      if (filters.date_to && new Date(doc.created_at) >= new Date(filters.date_to)) return false;

      return true;
    });

    // 3. Update ONLY the view
    setFilteredDocuments(filtered);

    // const newParams = { ...searchParams, filters, page: 1 };
    // performSearch(newParams);
  }, [searchParams, searchResults]);

  // Handle pagination
  const handlePageChange = useCallback((page: number) => {
    const newParams = { ...searchParams, page };
    performSearch(newParams);
  }, [searchParams, performSearch]);

  // Handle sorting
  const handleSortChange = useCallback((sort: 'score' | 'date' | 'title', order: 'asc' | 'desc') => {
    const newParams = { ...searchParams, sort, order, page: 1 };
    setSortOpen(false);
    performSearch(newParams);
  }, [searchParams, performSearch]);

  // Handle document actions
  const handlePreview = (document: any) => {
    setPreviewDocument(document);
    setIsPreviewOpen(true);
  };

  const handleDownload = async (document: any) => {
    try {
      const response = await api.get(`/api/documents/${document.id}/download`, { responseType: 'blob' });
      const blob = response.data as Blob;
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = document.file_name || `document-${document.id}`;
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Download started');
    } catch (error) {
      toast.error('Download failed');
      console.error('Download error:', error);
    }
  };

  const handleToggleFavorite = async (document: any) => {
    try {
      const isFavorite = favorites.has(document.id);

      if (isFavorite) {
        // Optimistically update UI (optional, but feels faster)
        // or wait for API:

        // 1. Call API to remove
        await documentService.removeFromFavorites(document.id);

        // 2. Update local state on success
        const newFavorites = new Set(favorites);
        newFavorites.delete(document.id);
        setFavorites(newFavorites);
        toast.success('Removed from favorites');
      } else {
        // 1. Call API to add
        await documentService.addToFavorites(document.id);

        // 2. Update local state on success
        const newFavorites = new Set(favorites);
        newFavorites.add(document.id);
        setFavorites(newFavorites);
        toast.success('Added to favorites');
      }
    } catch (error: any) {
      console.error('Failed to toggle favorite:', error);
      toast.error(error.message || 'Failed to update favorites');
    }
  };

  // Load facets when component mounts (only once)
  useEffect(() => {
    // Prevent multiple facet loads using ref and state
    if (facetsLoadedRef.current || facetsLoading) {
      console.log('Facets already loaded locally, skipping...');
      return;
    }

    console.log('Loading facets - this should only happen once');

    const loadFacets = async () => {
      try {
        setFacetsLoading(true); // Set loading state
        console.log('Starting facets API call...');
        const facets = await searchService.getFacets();
        console.log('Facets loaded successfully:', Object.keys(facets));

        // Mark as loaded locally
        facetsLoadedRef.current = true;

        // Update search results with facets even if no search has been performed
        setSearchResults((prev: SearchResponse | null) => prev ? { ...prev, facets } : {
          documents: [],
          total: 0,
          facets,
          pagination: { page: 1, per_page: 20, total_pages: 0 }
        });
      } catch (error) {
        console.error('Failed to load facets:', error);
      } finally {
        setFacetsLoading(false); // Clear loading state
      }
    };

    loadFacets();
  }, [facetsLoading]); // Include facetsLoading in dependencies

  // Disable auto-search on typing per requirements
  useEffect(() => {
    // no-op: search only runs when Search button is clicked
  }, [query]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Search Documents
              </h1>
              <p className="mt-1 text-gray-600 dark:text-gray-400">
                Find legal documents, cases, and legal resources
              </p>
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg transition-colors ${viewMode === 'list'
                  ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'
                  : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                  }`}
                title="List View"
              >
                <List className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg transition-colors ${viewMode === 'grid'
                  ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'
                  : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                  }`}
                title="Grid View"
              >
                <Grid className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Left Sidebar - Filters */}
          <div className="lg:col-span-1">
            <SearchFiltersPanel
              filters={searchParams.filters}
              onFiltersChange={handleFiltersChange}
              facets={searchResults?.facets || {}}
              isOpen={filtersOpen}
              onToggle={() => setFiltersOpen(!filtersOpen)}
            />
          </div>

          {/* Main Content - Search and Results */}
          <div className="lg:col-span-3 space-y-6">
            {/* Search Input */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <div className="flex gap-3 items-stretch">
                <div className="flex-1">
                  <SearchInput
                    query={query}
                    onQueryChange={setQuery}
                    onSearch={handleSearch}
                    placeholder="Search for legal documents, cases, statutes..."
                  />
                </div>
                <button
                  onClick={handleSearch}
                  className="px-5 py-3 rounded-lg bg-blue-600 text-white hover:bg-blue-700 self-stretch"
                  title="Search"
                >
                  Search
                </button>
              </div>
            </div>

            {/* Search Controls */}
            {filteredDocuments.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {filteredDocuments.length} results found
                    </span>
                    {Object.values(searchParams.filters).some(v => v) && (
                      <span className="text-sm text-blue-600 dark:text-blue-400">
                        Filters applied
                      </span>
                    )}
                  </div>

                  {/* Sort Controls */}
                  <div className="relative">
                    <button
                      onClick={() => setSortOpen(!sortOpen)}
                      className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                    >
                      {searchParams.order === 'desc' ? <SortDesc className="w-4 h-4" /> : <SortAsc className="w-4 h-4" />}
                      <span>Sort by {searchParams.sort}</span>
                    </button>

                    {sortOpen && (
                      <div className="absolute right-0 top-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-10 min-w-48">
                        <div className="py-1">
                          <button
                            onClick={() => handleSortChange('score', 'desc')}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-white"
                          >
                            Relevance (High to Low)
                          </button>
                          <button
                            onClick={() => handleSortChange('date', 'desc')}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-white"
                          >
                            Date (Newest First)
                          </button>
                          <button
                            onClick={() => handleSortChange('date', 'asc')}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-white"
                          >
                            Date (Oldest First)
                          </button>
                          <button
                            onClick={() => handleSortChange('title', 'asc')}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-white"
                          >
                            Title (A-Z)
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Search Results */}
            {isSearching ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                <p className="text-gray-600 dark:text-gray-400">Searching...</p>
              </div>
            ) : filteredDocuments && filteredDocuments.length > 0 ? (
              <div className="space-y-4">
                {filteredDocuments.map((result) => (
                  <SearchResultItem
                    key={result.id}
                    result={result}
                    onPreview={handlePreview}
                    onDownload={handleDownload}
                    onToggleFavorite={handleToggleFavorite}
                    isFavorite={favorites.has(result.id)}
                  />
                ))}
              </div>
            ) : filteredDocuments && filteredDocuments.length === 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12 text-center">
                <div className="text-gray-400 dark:text-gray-500 mb-4">
                  <div className="w-16 h-16 mx-auto">
                    <SearchIcon />
                  </div>
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  No results found
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Try adjusting your search terms or filters to find what you're looking for.
                </p>
              </div>
            ) : null}

            {/* Pagination */}
            {searchResults && searchResults.total > searchParams.per_page && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Showing {((searchParams.page - 1) * searchParams.per_page) + 1} to{' '}
                    {Math.min(searchParams.page * searchParams.per_page, searchResults.total)} of{' '}
                    {searchResults.total} results
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handlePageChange(searchParams.page - 1)}
                      disabled={searchParams.page === 1}
                      className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Previous
                    </button>

                    <span className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400">
                      Page {searchParams.page} of {searchResults.pagination.total_pages}
                    </span>

                    <button
                      onClick={() => handlePageChange(searchParams.page + 1)}
                      disabled={searchParams.page === searchResults.pagination.total_pages}
                      className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Document Preview Modal */}
      <DocumentPreviewModal
        document={previewDocument}
        isOpen={isPreviewOpen}
        onClose={() => {
          setIsPreviewOpen(false);
          setPreviewDocument(null);
        }}
        onDownload={handleDownload}
      />
    </div>
  );
};

export default Search;
